import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection } from "lexical";
import { colors2, UI_BODY_SM_DF, useScroll } from "oolib";
import { Highlighter } from "@phosphor-icons/react";

import { SHOW_ANNO_LIGHTBOX_COMMAND } from "../AnnoPlugin";

// Dark rounded pill anchored above the selection — primary background, white
// content, soft shadow.
const AnnoPillButton = styled.button`
  position: fixed;
  transform: translate(-50%, -100%);
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  border-radius: 16px;
  background: ${colors2.grey90};
  color: ${colors2.white};
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.15);
  }
`;

const AnnoPillLabel = styled(UI_BODY_SM_DF)`
  color: inherit;
`;

/**
 * Mounted by LexicalTextEditor when readOnly + enableAnnotationEditOnReadOnly.
 *
 * Tracks whether a non-collapsed range selection exists and renders a small
 * floating button near the selection. Clicking it dispatches the existing
 * SHOW_ANNO_LIGHTBOX_COMMAND, which AnnoPlugin handles (in turn flipping
 * editable=true via the bracket effect) and opens the existing AnnoLightbox.
 *
 * Selection-loss avoidance: onMouseDown calls preventDefault so the click
 * doesn't move focus off the editor. Without this, the editor's range
 * selection is dropped before AnnoLightbox can capture it on mount.
 */
export const FloatingAnnoButtonPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [pos, setPos] = useState(null);

  // Computes the button's viewport position from the current DOM selection.
  // Returns null when no usable selection exists.
  const computePosFromSelection = () => {
    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) return null;
    const rect = domSelection.getRangeAt(0).getBoundingClientRect();
    if (!rect.width && !rect.height) return null;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    };
  };

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          setPos(null);
          return;
        }
        setPos(computePosFromSelection());
      });
    });
  }, [editor]);

  // Track scrolls anywhere in the DOM (mirroring oolib's
  // usePopOutOfOverflowHiddenParent pattern). detectScrollInAllDivs registers
  // the listener in capture phase on window — scroll events don't bubble,
  // so capture is what lets a window-level listener catch scrolls inside any
  // descendant scroll container.
  useScroll(
    () => setPos((prev) => (prev ? computePosFromSelection() : prev)),
    { noThrottle: true, detectScrollInAllDivs: true }
  );

  // Resize doesn't go through useScroll — handle separately.
  useEffect(() => {
    const onResize = () => setPos((prev) => (prev ? computePosFromSelection() : prev));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Hide the pill when the selection is dropped by a click outside the editor.
  // In annotate-only mode the editor is non-editable, so collapsing the DOM
  // selection that way doesn't fire a Lexical update — only the native
  // selectionchange event does. This handler only ever hides; showing stays
  // driven by the Lexical update listener above. (The pill's own mousedown
  // preventDefault keeps the selection alive, so clicking it won't trigger
  // this.)
  useEffect(() => {
    const onSelectionChange = () => {
      const domSelection = window.getSelection();
      if (!domSelection || domSelection.isCollapsed || domSelection.rangeCount === 0) {
        setPos(null);
      }
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  if (!pos) return null;

  const handleMouseDown = (e) => {
    // Prevent focus shift so the editor's selection survives until
    // AnnoLightbox captures it.
    e.preventDefault();
  };

  const handleClick = (e) => {
    e.stopPropagation();
    // Dismiss the pill as soon as the lightbox is requested.
    setPos(null);
    editor.dispatchCommand(SHOW_ANNO_LIGHTBOX_COMMAND, undefined);
  };

  return createPortal(
    <AnnoPillButton
      type="button"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{ left: pos.x, top: pos.y }}
    >
      <Highlighter size={16} weight="regular" color="currentColor" />
      <AnnoPillLabel>Annotate</AnnoPillLabel>
    </AnnoPillButton>,
    document.body
  );
};
